import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Star, CreditCard } from "lucide-react";
import { api, errMsg, Booking, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, SandboxBadge, EmptyState } from "@/components/site/Bits";

const ReviewForm = ({ b, email, onDone }: { b: Booking; email: string; onDone: () => void }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const send = async () => {
    try {
      const r = await api.post("/reviews", { reference: b.reference, email, rating, comment });
      toast.success(r.data.message);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 space-y-3 rounded-xl bg-stone-canvas p-4" data-testid="review-form">
      <p className="text-sm font-semibold">Share an honest review</p>
      <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setRating(n)} data-testid={`review-star-${n}`}><Star size={20} className={n <= rating ? "fill-gold text-gold" : "text-navy/25"} /></button>)}</div>
      <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tell future guests about your experience (min 10 characters)" className="field" data-testid="review-comment" />
      <button onClick={send} className="btn-navy" data-testid="review-submit">Submit review</button>
    </div>
  );
};

export const BookingCard = ({ b, email, onChange }: { b: Booking; email?: string; onChange?: (b: Booking) => void }) => {
  const [pay, setPay] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  useEffect(() => {
    api.get("/payments/config").then((r) => setPay(r.data)).catch(() => {});
  }, []);
  const cancel = async () => {
    if (!email || !window.confirm("Cancel this request?")) return;
    try {
      const r = await api.post("/bookings/lookup/cancel", { reference: b.reference, email });
      onChange?.(r.data);
      toast.success("Booking cancelled");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const checkout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", { reference: b.reference, email: email || b.customer.email, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const awaitingPay = ["approved", "confirmed"].includes(b.status) && !["paid", "deposit_paid"].includes(b.payment_status);
  return (
    <div className="card-lux p-6 sm:p-8" data-testid={`booking-card-${b.reference}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-sm font-bold tracking-wider" data-testid="lookup-reference">{b.reference}</p>
          <h3 className="mt-1 text-2xl">{b.item_title}</h3>
          <p className="mt-1 text-sm text-navy/60">{b.city} · {fmtDate(b.start_date)} → {fmtDate(b.end_date)} · {b.units} {b.unit_label}s · {b.guests} {b.kind === "stay" ? "guests" : "passengers"}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={b.status} testid="lookup-status" />
          <span className="text-xs text-navy/55">Payment: <StatusBadge status={b.payment_status} testid="lookup-payment-status" /></span>
        </div>
      </div>
      {b.info_requested && <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="lookup-info-requested">Our team needs more information. Please check your email or contact support.</p>}
      <div className="mt-5 flex justify-between border-t border-navy/10 pt-4 text-sm"><span>Estimated total</span><span className="font-mono font-bold">{money(b.pricing.total, b.pricing.currency)}</span></div>
      <ol className="mt-5 space-y-2 border-l border-navy/15 pl-4" data-testid="lookup-history">
        {b.history.map((h, i) => <li key={i} className="text-sm"><span className="font-semibold capitalize">{h.status}</span> <span className="text-navy/50">· {fmtDate(h.at)}</span>{h.note ? <span className="block text-navy/65">{h.note}</span> : null}</li>)}
      </ol>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {email && ["pending", "approved"].includes(b.status) && !["paid", "deposit_paid"].includes(b.payment_status) && <button onClick={cancel} className="btn-ghost" data-testid="lookup-cancel-button">Cancel request</button>}
        {awaitingPay && pay?.enabled && (
          <>
            <button onClick={checkout} disabled={busy} className="btn-gold" data-testid="lookup-pay-button">{busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay {pay.deposit_percent < 100 ? `${pay.deposit_percent}% deposit` : "now"}</button>
            {!pay.live && <SandboxBadge />}
          </>
        )}
        {awaitingPay && pay && !pay.enabled && <p className="text-sm text-navy/60" data-testid="lookup-payment-offline">Online payment is not yet available. Our team will send payment instructions directly.</p>}
        {email && b.status === "completed" && !b.has_review && !reviewOpen && <button onClick={() => setReviewOpen(true)} className="btn-ghost" data-testid="lookup-review-button"><Star size={15} /> Write a review</button>}
      </div>
      {reviewOpen && email && <ReviewForm b={b} email={email} onDone={() => { setReviewOpen(false); onChange?.({ ...b, has_review: true }); }} />}
    </div>
  );
};

export default function MyBookings() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [email, setEmail] = useState("");
  const [b, setB] = useState<Booking | null>(null);
  const [busy, setBusy] = useState(false);
  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { setB((await api.post("/bookings/lookup", { reference: ref, email })).data); } catch (err) { setB(null); toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Secure lookup" title="My Bookings" intro="Enter your booking reference and the email you used to request. You will see the live status from our team." />
      <div className="container-x grid gap-10 py-12 lg:grid-cols-[380px_1fr]">
        <div className="space-y-6">
          <form onSubmit={lookup} className="card-lux space-y-4 p-6" data-testid="lookup-form">
            <div><label className="label">Booking reference</label><input required value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="TL-S-XXXXXXX" className="field font-mono" data-testid="lookup-reference-input" /></div>
            <div><label className="label">Email</label><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="field" data-testid="lookup-email-input" /></div>
            <button disabled={busy} className="btn-navy w-full" data-testid="lookup-submit">{busy && <Loader2 size={16} className="animate-spin" />} Find booking</button>
          </form>
          <div className="card-lux p-6 text-sm text-navy/65">
            {user ? <>Signed in as {user.email}. <Link to="/account" className="font-semibold underline" data-testid="lookup-account-link">View your booking history</Link></>
              : <><Link to="/register" className="font-semibold underline">Create an account</Link> to keep a history of requests made while signed in.</>}
          </div>
        </div>
        <div>{b ? <BookingCard b={b} email={email} onChange={setB} /> : <EmptyState testid="lookup-empty" title="Look up a booking" body="Your reference was shown after submitting and included in your acknowledgment email." />}</div>
      </div>
    </div>
  );
}
